import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import FeaturedProducts from '../components/FeaturedProducts';
import CategoryCarousel from '../components/CategoryCarousel';
import '../styles/Home.css';

const Home = () => {
  const [categoriesWithProducts, setCategoriesWithProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Variables configurables desde el environment
  const heroTitle = process.env.REACT_APP_HERO_TITLE || 'Bienvenido a Nuestra Tienda';
  const heroSubtitle = process.env.REACT_APP_HERO_SUBTITLE || 'Encuentra los mejores productos al mejor precio. Consulta por WhatsApp y recibe atención personalizada.';
  const featuredTitle = process.env.REACT_APP_FEATURED_TITLE || 'Novedades';
  const featuredSubtitle = process.env.REACT_APP_FEATURED_SUBTITLE || 'Los productos más populares de nuestra tienda';
  const ctaTitle = process.env.REACT_APP_CTA_TITLE || '¿Necesitas ayuda para elegir?';
  const ctaText = process.env.REACT_APP_CTA_TEXT || 'Nuestro equipo está disponible para asesorarte. Contáctanos por WhatsApp y te ayudaremos a encontrar el producto perfecto para ti.';

  useEffect(() => {
    loadCategoriesAndProducts();
  }, []);

  const loadCategoriesAndProducts = async () => {
    try {
      setLoading(true);
      
      // Obtener productos de la API
      const productsData = await apiClient.get('/ecommerce/product');
      
      if (Array.isArray(productsData)) {
        // Agrupar productos por categoría
        const productsByCategory = {};
        
        productsData.forEach(product => {
          // Solo incluir productos que tengan una categoría válida
          if (product.categoria && product.categoria.trim() !== '') {
            const categoria = product.categoria;
            
            if (!productsByCategory[categoria]) {
              productsByCategory[categoria] = [];
            }
            
            productsByCategory[categoria].push(product);
          }
        });
        
        // Convertir a array y ordenar categorías con más productos primero
        const categoriesArray = Object.entries(productsByCategory)
          .map(([category, products]) => ({
            category,
            products: products.sort((a, b) => {
              // Priorizar productos con imágenes
              const aHasImage = a.imagenes && a.imagenes.length > 0;
              const bHasImage = b.imagenes && b.imagenes.length > 0;
              
              if (aHasImage && !bHasImage) return -1;
              if (!aHasImage && bHasImage) return 1;
              return 0;
            })
          }))
          .sort((a, b) => b.products.length - a.products.length);
        
        setCategoriesWithProducts(categoriesArray);
      }
    } catch (error) {
      // Error silencioso
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            {heroTitle}
          </h1>
          <p className="hero-subtitle">
            {heroSubtitle}
          </p>
          <div className="hero-buttons">
            <Link to="/catalogo" className="btn btn-primary btn-large">
              Ver Catálogo Completo
            </Link>
            <a 
              href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || 'sin numero'}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-whatsapp btn-large"
            >
              <span style={{ fontSize: '1.2em' }}>💬</span>
              Contactar por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="featured-section">
        <div className="section-header">
          <h2 className="section-title">{featuredTitle}</h2>
          <p className="section-subtitle">{featuredSubtitle}</p>
        </div>
        
        <FeaturedProducts />
        
        <div className="section-footer">
          <Link to="/catalogo" className="btn btn-secondary">
            Ver Más Productos →
          </Link>
        </div>
      </section>

      {/* Categories Section */}
      {/* Valido para mostrar solo si contiene productos */}
      {categoriesWithProducts.length > 0 && (
        <section className="categories-section">
          {loading ? (
            <div className="categories-loading">
              <div className="spinner"></div>
              <p>Cargando productos...</p>
            </div>
          ) : categoriesWithProducts.length === 0 ? (
            <div className="categories-empty">
              <p>No hay productos disponibles</p>
            </div>
          ) : (
            <div className="categories-carousels">
              {categoriesWithProducts.map(({ category, products }) => (
                <CategoryCarousel 
                  key={category} 
                  category={category} 
                  products={products} 
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">{ctaTitle}</h2>
          <p className="cta-text">
            {ctaText}
          </p>
          <a 
            href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || 'sin numero'}?text=${encodeURIComponent('Hola! Necesito ayuda para elegir un producto.')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-whatsapp btn-large"
          >
            <span style={{ fontSize: '1.2em' }}>💬</span>
            Chatear con un Asesor
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
